import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GoalIcon } from '../icons/feature-icons';
import { useFestaGiornataStore, type MotivoFesta } from '../../store/festa-giornata';
import { vibrate } from '../../utils/vibration';
import './FestaGiornata.css';

/**
 * Quanto resta in scena un motivo, prima di cominciare a uscire.
 *
 * Tre secondi e non meno: il riempimento dell'emblema ne dura due — e' il fuoco
 * dell'animazione, e piu' corto passava prima che l'occhio lo seguisse — quindi
 * sotto i 3s l'emblema finirebbe di colorarsi mentre gia' esce. Il legame non
 * e' lasciato alla memoria: `festa-giornata.stile.test.ts` confronta questo
 * numero con la durata e il ritardo del riempimento.
 */
const DURATA_MS = 3000;

/**
 * Quanto dura l'uscita.
 *
 * ⚠️ Deve combaciare con `--festa-uscita` in `FestaGiornata.css`, e non e'
 * affidato alla buona memoria: `festa-giornata.stile.test.ts` confronta i due
 * numeri. Un doppione fra TS e CSS si chiude con un filo, non con la disciplina.
 */
const DURATA_USCITA_MS = 280;

/** Le otto scintille dell'anello. Angolo e ritardo di ciascuna li decide il
 *  foglio con `:nth-child()`: qui serve solo che siano otto. */
const SCINTILLE = [0, 1, 2, 3, 4, 5, 6, 7];

/**
 * La fiamma, disegnata a mano e non importata.
 *
 * `StreakIcon` di `feature-icons.tsx` esiste, ma ha i colori in esadecimale
 * cablato; questa li prende dai token, quindi segue i due temi. Si disegna due
 * volte — una copia spenta e una accesa — ed e' per questo che sta in una
 * funzione invece che inline.
 */
function Fiamma() {
  return (
    <svg className="festa-fiamma" viewBox="0 0 48 64" aria-hidden="true">
      <path
        className="festa-fiamma-fuori"
        d="M24 2c6 10 2 14 6 19 3 4 9 7 9 17 0 13-9 24-21 24S0 51 0 38c0-9 5-13 8-19 2 4 4 6 7 6-3-9 3-17 9-23z"
        transform="translate(4 0)"
      />
      <path
        className="festa-fiamma-dentro"
        d="M24 26c3 5 1 7 3 10 2 2 5 4 5 9 0 7-5 13-11 13s-11-6-11-13c0-5 3-7 4-10 1 2 2 3 4 3-2-5 2-9 6-12z"
        transform="translate(4 0)"
      />
    </svg>
  );
}

/**
 * L'EMBLEMA CHE SI COLORA DAL BASSO.
 *
 * Due copie sovrapposte della stessa icona: sotto quella spenta (grigia), sopra
 * quella a colori, ritagliata da un `clip-path` che si apre dal basso verso
 * l'alto. Tre scelte, e nessuna e' arbitraria:
 *
 * 1. Il grigio sta sul WRAPPER, non sull'`<svg>`. Ogni icona di
 *    `feature-icons.tsx` porta un `style={depth}` IN LINEA con un `drop-shadow`,
 *    e uno stile in linea batte qualunque `filter` messo da una classe. E' lo
 *    stesso motivo per cui `DailyGoalCard` e `StreakCard` avvolgono le loro
 *    icone in un `div`.
 * 2. Due copie non si pestano i piedi: le icone generano gli id dei gradienti
 *    con `useId()`, apposta perche' due istanze possano convivere.
 * 3. Il ritaglio sta su una scatola HTML e non su un nodo SVG: li' il
 *    riferimento del `clip-path` e' il `border-box`, uguale su ogni motore.
 */
function Emblema({ tipo }: { tipo: MotivoFesta['tipo'] }) {
  const icona = tipo === 'streak' ? <Fiamma /> : <GoalIcon size={56} />;
  return (
    <div className={`festa-emblema festa-emblema--${tipo}`} aria-hidden="true">
      <div className="festa-emblema-strato festa-emblema-spento">{icona}</div>
      <div className="festa-emblema-strato festa-emblema-acceso">{icona}</div>
    </div>
  );
}

/**
 * IL VELO DELLA GIORNATA: lo Streak rinnovato e/o l'obiettivo raggiunto.
 *
 * Esce con un PORTALE su `document.body`, come `GameOverOverlay`, ed e' bene
 * dire con precisione CONTRO COSA — perche' la spiegazione che viene in mente
 * per prima e' sbagliata, ed e' stata misurata.
 *
 * Questo componente e' montato una volta sola dentro `challengeOverlays`
 * (App.tsx), che App.tsx innesta in una trentina di schermate; in un ramo quel
 * frammento e' figlio di `.pz-inner`, che porta `max-width: 1080px`. Quel tetto
 * NON lo tocca: per una scatola `position: fixed` il blocco contenitore e' il
 * viewport, e il `max-width` di un antenato non la raggiunge. Misurato a 1280px
 * sulla pagina viva dei problemi: una sonda `fixed; inset: 0` dentro
 * `.pz-inner` misura 1280 a x=0, esattamente come in fondo a `body`.
 *
 * Il pericolo vero e' una regola `X > *`, che cade sul velo STESSO:
 * `.game-replay > *` gli dava `max-width: 680px; margin: auto` e lo riduceva a
 * una banda centrata (da 681px in su la pagina restava scoperta ai due lati), e
 * `.dash > :not(.modal-overlay)` gli darebbe `position: relative`, che annulla
 * il `fixed` in tronco. Tutte e due sono gia' curate, quindi oggi nessun
 * ospite di `challengeOverlays` lo strizza — e il portale serve a tenerlo vero
 * per gli ospiti che non esistono ancora, al prezzo di una riga.
 * E' la stessa famiglia di difetti da cui nasce
 * `veli-a-tutto-schermo.stile.test.ts`, gia' pagata due volte.
 *
 * NON si iscrive a `useOverlayBackButton`: quell'hook spinge una voce di
 * cronologia, e per un velo cosmetico che vive pochi secondi il rischio —
 * uscire dall'app al primo «indietro» — e' peggiore del beneficio.
 */
function FestaGiornata() {
  const { t } = useTranslation();
  const coda = useFestaGiornataStore((s) => s.coda);
  const fase = useFestaGiornataStore((s) => s.fase);
  const chiudi = useFestaGiornataStore((s) => s.chiudi);
  const congeda = useFestaGiornataStore((s) => s.congeda);

  const corrente = coda[0] ?? null;
  /* L'ultimo della coda: solo lui porta via anche il fondale. Vedi sotto. */
  const ultimo = coda.length <= 1;

  /*
   * La vibrazione e' UNA PER SEQUENZA, non una per motivo: segna l'arrivo della
   * festa, non ogni sua pagina. Il riferimento ricorda se il velo era gia'
   * aperto, perche' fra un motivo e l'altro la coda non passa mai da vuota.
   */
  const eraAperto = useRef(false);
  useEffect(() => {
    if (corrente && !eraAperto.current) vibrate(40);
    eraAperto.current = corrente !== null;
  }, [corrente]);

  // Ogni motivo vive il suo tempo e poi comincia a uscire. La chiave
  // dell'effetto e' il motivo: quello dopo riarma il timer da capo.
  useEffect(() => {
    if (!corrente || fase === 'uscita') return;
    const timer = window.setTimeout(chiudi, DURATA_MS);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') chiudi(); };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
    };
  }, [corrente, fase, chiudi]);

  /*
   * La fine dell'uscita, A TIMER E NON SU `animationend`.
   *
   * La ragione e' gia' scritta in casa, in `hooks/useLente.ts`: «un timer e non
   * `transitionend`: se la transizione non parte, l'evento non arriva mai».
   * Qui succede esattamente — con `prefers-reduced-motion: reduce`
   * l'animazione d'uscita e' `none`, `animationend` non scatterebbe MAI e il
   * velo resterebbe a schermo per sempre.
   */
  useEffect(() => {
    if (fase !== 'uscita') return;
    const timer = window.setTimeout(congeda, DURATA_USCITA_MS);
    return () => window.clearTimeout(timer);
  }, [fase, congeda]);

  if (!corrente) return null;

  const streak = corrente.tipo === 'streak';
  /* Il fondale sfocato se ne va UNA VOLTA SOLA, alla fine della coda: fra un
     motivo e l'altro esce la sola scheda. Due dissolvenze piene del velo a
     pochi secondi l'una dall'altra sarebbero uno sfarfallio, non una sequenza. */
  const veloEsce = fase === 'uscita' && ultimo;

  return createPortal(
    <div
      className={`festa-giornata-velo ${veloEsce ? 'is-uscita' : ''}`}
      role="status"
      aria-live="polite"
      onClick={chiudi}
      data-testid="festa-giornata"
    >
      {/* Il `key` non e' decorativo: senza, React riusa lo stesso nodo e le
          animazioni d'ingresso del motivo seguente NON ripartono — entrerebbe
          gia' fermo e gia' a colori. */}
      <div
        key={`${corrente.tipo}-${corrente.numero}`}
        className={`festa-giornata-card ${fase === 'uscita' ? 'is-uscita' : ''}`}
      >
        <div className="festa-anello">
          {SCINTILLE.map((i) => (
            <span key={i} className="festa-scintilla" aria-hidden="true" />
          ))}
          <Emblema tipo={corrente.tipo} />
        </div>

        {/* «Streak» resta il letterale: termine di brand, come in StreakCard.tsx
            e nell'eroe dei problemi. Bloccato da i18n-brand-terms.test.ts. */}
        <div className="festa-eyebrow">
          {streak ? 'Streak' : t('dashboard.dailyGoal.title')}
        </div>
        <div className="festa-numero">{corrente.numero}</div>

        {/* Le tre righe che il telefono non mostra (il foglio le spegne sotto i
            768px): li' restano emblema, titolo e numero, e basta. */}
        <div className="festa-riga">
          {streak
            ? t('dashboard.streak.daysInRow', { count: corrente.numero })
            : t('dashboard.dailyGoal.done')}
        </div>
        <div className="festa-sub">
          {streak
            ? t('dashboard.streak.festa.subtitle')
            : t('dashboard.dailyGoal.festa.subtitle')}
        </div>
        <div className="festa-chiudi">{t('dashboard.festa.dismiss')}</div>
      </div>
    </div>,
    document.body,
  );
}

export default FestaGiornata;
